import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children, user }) {
  const [permissions, setPermissions] = useState(null);
  const [userLevel, setUserLevel] = useState('volunteer');
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const eventId = localStorage.getItem('selectedEventId');
  const isEventArchived = localStorage.getItem('selectedEventArchived') === 'true';

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const url = eventId 
          ? `${API}/my-module-permissions?event_id=${eventId}`
          : `${API}/my-module-permissions`;
          
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setPermissions(res.data.permissions || {});
        setUserLevel(res.data.user_level || 'volunteer');
      } catch (error) {
        console.error('Kunne ikke hente rettigheder:', error);
        // Fallback til default rettigheder for frivillige
        setPermissions({});
        setUserLevel('volunteer');
      }
      setLoading(false);
    };

    fetchPermissions();
  }, [token, eventId, user?.email]);

  // Helper funktion til at tjekke en specifik rettighed
  const hasPermission = (permKey) => {
    // Admin har altid adgang
    if (userLevel === 'admin' || user?.role === 'admin') {
      return true;
    }
    const value = permissions?.[permKey];
    // Håndter forskellige værdityper:
    // - true/false (booleans)
    // - "ja"/"alle" (gamle strenge) -> true
    // - "kun_eget_omraade" -> true (delvis adgang, men stadig adgang)
    // - "nej" -> false
    if (value === true || value === "ja" || value === "alle" || value === "kun_eget_omraade") {
      return true;
    }
    return false;
  };

  // Ny helper til at tjekke om bruger kun har adgang til eget område
  const hasOnlyOwnAreaAccess = (permKey) => {
    if (userLevel === 'admin' || user?.role === 'admin') {
      return false; // Admin har fuld adgang
    }
    return permissions?.[permKey] === "kun_eget_omraade";
  };

  // Helper til at tjekke modul-adgang
  const canAccessModule = (moduleName) => {
    const moduleKey = `modul_${moduleName}`;
    return hasPermission(moduleKey);
  };

  // Helper til at tjekke handling
  const canPerformAction = (actionKey) => {
    if (isEventArchived) return false;
    return hasPermission(actionKey);
  };

  const value = {
    permissions,
    userLevel,
    loading,
    hasPermission,
    hasOnlyOwnAreaAccess,
    canAccessModule,
    canPerformAction,
    isAdmin: userLevel === 'admin' || user?.role === 'admin',
    isAdminAssistant: userLevel === 'admin_assistant' || user?.role === 'admin_assistant',
    isLeader: userLevel === 'leader',
    isVolunteer: userLevel === 'volunteer',
    isEventArchived,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

export default PermissionsContext;
